/**
 * WorkspaceSettingCredentials — sl-vgk8.2
 *
 * Workspace settings page for managing third-party service credentials.
 * Supports API Keys, OAuth2, and Basic Auth.
 * Full CRUD: Create, Read (masked), Update, Delete with confirmation.
 */
import { Button, IconButton, Input } from '@affine/component';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { InvisibleIcon, ViewIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { ConnectionSettingsSection } from '../../../../../modules/streamlain/connections/connection-settings-section';
import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import {
  SettingsActionRow,
  SettingsActionsSection,
  SettingsCodeBlock,
  SettingsConfirmButton,
  SettingsContentDivider,
  SettingsContentSection,
  SettingsContentSurface,
  SettingsContentText,
  SettingsTabbedContent,
} from '../shared/settings-content';
import { SettingsEditorScopeHint } from '../shared/settings-editor-scope-hint';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import { SettingsSearchableEntityPage } from '../shared/settings-entity-page';
import { SettingsEntityFormDialog } from '../shared/settings-form-dialog';
import { SettingsTabs } from '../shared/settings-navigation';
import {
  AddCredentialModal,
  CredentialFormFields,
  TYPE_LABELS,
  useCredentialForm,
} from './add-credential-modal';
import { AdminOverviewTab } from './admin-overview-tab';
import { CredentialPermissionsEditor } from './credential-permissions-editor';
import * as styles from './index.css';
import { TransferOwnershipModal } from './transfer-ownership-modal';
import {
  type CredentialData,
  type CredentialType,
  type CredentialUsageInfo,
  useWorkspaceCredentials,
  type WorkspaceCredentialInfo,
} from './use-workspace-credentials';

// =============================================================================
// Helpers
// =============================================================================

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatCredentialSecret(
  type: CredentialType,
  data: CredentialData
): string {
  switch (type) {
    case 'api_key':
      return (data as { key?: string }).key ?? '';
    case 'basic_auth': {
      const value = data as { username?: string; password?: string };
      return `username: ${value.username ?? ''}\npassword: ${value.password ?? ''}`;
    }
    case 'oauth2': {
      const value = data as {
        clientId?: string;
        clientSecret?: string;
        tokenUrl?: string;
        accessToken?: string;
        refreshToken?: string;
        expiresAt?: string;
        scopes?: string[];
      };
      return [
        `clientId: ${value.clientId ?? ''}`,
        `clientSecret: ${value.clientSecret ?? ''}`,
        `tokenUrl: ${value.tokenUrl ?? ''}`,
        ...(value.scopes?.length ? [`scopes: ${value.scopes.join(', ')}`] : []),
        ...(value.accessToken ? [`accessToken: ${value.accessToken}`] : []),
        ...(value.refreshToken ? [`refreshToken: ${value.refreshToken}`] : []),
        ...(value.expiresAt ? [`expiresAt: ${value.expiresAt}`] : []),
      ].join('\n');
    }
    default:
      return '';
  }
}

function formatCredentialSecretInline(
  type: CredentialType,
  data: CredentialData
): string {
  switch (type) {
    case 'api_key':
      return (data as { key?: string }).key ?? '';
    case 'basic_auth': {
      const value = data as { username?: string; password?: string };
      return `${value.username ?? ''}:${value.password ?? ''}`;
    }
    case 'oauth2': {
      const value = data as {
        clientId?: string;
        clientSecret?: string;
        tokenUrl?: string;
        accessToken?: string;
        refreshToken?: string;
      };
      return [
        value.clientId ? `clientId=${value.clientId}` : null,
        value.clientSecret ? `clientSecret=${value.clientSecret}` : null,
        value.tokenUrl ? `tokenUrl=${value.tokenUrl}` : null,
        value.accessToken ? `accessToken=${value.accessToken}` : null,
        value.refreshToken ? `refreshToken=${value.refreshToken}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    }
    default:
      return formatCredentialSecret(type, data);
  }
}

// =============================================================================
// Edit Credential Modal
// =============================================================================

function EditCredentialModal({
  credential,
  onClose,
  onSave,
  isSaving,
}: {
  credential: WorkspaceCredentialInfo;
  onClose: () => void;
  onSave: (credentialId: string, data: CredentialData) => Promise<void>;
  isSaving: boolean;
}) {
  const { state, setField, canSave, buildData } = useCredentialForm(
    credential.type
  );

  const handleSave = useCallback(async () => {
    await onSave(credential.id, buildData());
    onClose();
  }, [credential.id, buildData, onSave, onClose]);

  return (
    <SettingsEntityFormDialog
      open={true}
      onOpenChange={v => {
        if (!v) onClose();
      }}
      title={`Edit Credential: ${credential.name}`}
      onSave={() => void handleSave()}
      onCancel={onClose}
      saveLabel="Update"
      cancelLabel="Cancel"
      saveDisabled={!canSave || isSaving}
      saving={isSaving}
      savingLabel="Saving..."
    >
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Type</label>
        <div className={styles.fieldReadonly}>
          {TYPE_LABELS[credential.type]}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Service</label>
        <div className={styles.fieldReadonly}>{credential.service}</div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Current Value</label>
        <code className={styles.maskedHint}>{credential.maskedHint}</code>
      </div>

      <div style={{ margin: '16px 0 8px', fontSize: '13px', fontWeight: 500 }}>
        Enter new credentials:
      </div>

      <CredentialFormFields
        type={credential.type}
        state={state}
        setField={setField}
      />
    </SettingsEntityFormDialog>
  );
}

// =============================================================================
// Credential Card
// =============================================================================

function CredentialCard({
  credential,
  onEdit,
  onDelete,
  onConnect,
  onGetUsage,
  onReveal,
  onTransfer,
  canTransfer,
  isAdmin,
  isSaving,
  canManageAccess,
}: {
  credential: WorkspaceCredentialInfo;
  onEdit: (cred: WorkspaceCredentialInfo) => void;
  onDelete: (
    id: string,
    name: string,
    force?: boolean
  ) => Promise<{ deleted: boolean; usages?: CredentialUsageInfo[] }>;
  onConnect: (credentialId: string, service: string) => void;
  onGetUsage: (credentialId: string) => Promise<CredentialUsageInfo[]>;
  onReveal: (credentialId: string) => Promise<CredentialData>;
  onTransfer: (cred: WorkspaceCredentialInfo) => void;
  canTransfer: boolean;
  isAdmin: boolean;
  isSaving: boolean;
  canManageAccess: boolean;
}) {
  const t = useI18n();
  const [usageConflict, setUsageConflict] = useState<CredentialUsageInfo[]>([]);
  const [usageCount, setUsageCount] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'manage' | 'access'>('manage');
  const [revealedData, setRevealedData] = useState<CredentialData | null>(null);
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void onGetUsage(credential.id).then(usages => {
      if (!cancelled) setUsageCount(usages.length);
    });
    return () => {
      cancelled = true;
    };
  }, [credential.id, onGetUsage]);

  useEffect(() => {
    if (!expanded) {
      setActiveTab('manage');
      setIsSecretVisible(false);
    }
  }, [expanded]);

  const usageLabel =
    usageCount != null && usageCount > 0
      ? `Used in ${usageCount} workflow${usageCount !== 1 ? 's' : ''}`
      : 'Not used';

  const statusBadgeVariant: 'active' | 'error' =
    credential.status === 'active' ? 'active' : 'error';

  const handleSecretToggle = useCallback(async () => {
    if (isSecretVisible) {
      setIsSecretVisible(false);
      return;
    }

    if (revealedData) {
      setIsSecretVisible(true);
      return;
    }

    setIsRevealing(true);
    try {
      const data = await onReveal(credential.id);
      setRevealedData(data);
      setIsSecretVisible(true);
    } finally {
      setIsRevealing(false);
    }
  }, [credential.id, isSecretVisible, onReveal, revealedData]);

  return (
    <SettingsExpandableItem
      dataTestId={`credential-item-${credential.id}`}
      expanded={expanded}
      onToggle={() => setExpanded(v => !v)}
      title={credential.name}
      badges={
        <>
          <SettingsBadge variant={statusBadgeVariant}>
            {credential.status === 'active'
              ? (t['com.streamlain.streamlain.common.active']?.() ?? 'Aktiv')
              : credential.status === 'expired'
                ? 'Expired'
                : 'Error'}
          </SettingsBadge>
          <SettingsBadge>{usageLabel}</SettingsBadge>
        </>
      }
      meta={
        <>
          <SettingsMetaItem>
            {TYPE_LABELS[credential.type] ?? credential.type}
          </SettingsMetaItem>
          <SettingsMetaItem>{credential.service}</SettingsMetaItem>
          <SettingsMetaItem>{credential.maskedHint}</SettingsMetaItem>
          {credential.lastUsedAt ? (
            <SettingsMetaItem>
              {`Last used ${formatTimeAgo(new Date(credential.lastUsedAt))}`}
            </SettingsMetaItem>
          ) : null}
        </>
      }
    >
      <SettingsTabbedContent>
        <SettingsTabs
          ariaLabel="Credential sections"
          activeTab={activeTab}
          onChange={tabId => setActiveTab(tabId as 'manage' | 'access')}
          tabs={[
            {
              id: 'manage',
              label:
                t[
                  'com.streamlain.settings.workspace.webhooks.card.management'
                ]?.() ?? 'Manage endpoint',
            },
            ...(canManageAccess
              ? [
                  {
                    id: 'access',
                    label:
                      t[
                        'com.streamlain.settings.workspace.webhooks.card.access'
                      ]?.() ?? 'Permissions and access control',
                  },
                ]
              : []),
          ]}
        />

        {activeTab === 'manage' && (
          <SettingsContentSurface>
            <SettingsContentSection title="Credential Details">
              <Input
                value={
                  isSecretVisible && revealedData
                    ? formatCredentialSecretInline(
                        credential.type,
                        revealedData
                      )
                    : credential.maskedHint
                }
                readOnly
                spellCheck={false}
                inputStyle={{
                  fontFamily:
                    'ui-monospace, SFMono-Regular, SF Mono, Menlo, Monaco, Consolas, Liberation Mono, monospace',
                }}
                endFix={
                  credential.canRevealSecret ? (
                    <IconButton
                      tooltip={
                        isSecretVisible ? 'Hide secret' : 'Reveal secret'
                      }
                      disabled={isRevealing}
                      onClick={() => void handleSecretToggle()}
                      size={20}
                    >
                      {isSecretVisible ? <InvisibleIcon /> : <ViewIcon />}
                    </IconButton>
                  ) : undefined
                }
              />
              {credential.lastError ? (
                <SettingsEntityMessage>
                  {credential.lastError}
                </SettingsEntityMessage>
              ) : null}
            </SettingsContentSection>

            {usageConflict.length > 0 ? (
              <>
                <SettingsContentDivider />
                <SettingsContentSection title="Usage conflict">
                  <SettingsContentText>
                    {`Used by ${usageConflict.length} workflow${usageConflict.length !== 1 ? 's' : ''}.`}
                  </SettingsContentText>
                  <SettingsCodeBlock as="pre">
                    {usageConflict
                      .slice(0, 3)
                      .map(u => `- ${u.workflowName}`)
                      .join('\n')}
                  </SettingsCodeBlock>
                  <SettingsActionRow>
                    <Button
                      variant="error"
                      disabled={isSaving}
                      onClick={() => {
                        void onDelete(
                          credential.id,
                          credential.name,
                          true
                        ).then(() => setUsageConflict([]));
                      }}
                    >
                      Force Delete
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setUsageConflict([])}
                    >
                      Cancel
                    </Button>
                  </SettingsActionRow>
                </SettingsContentSection>
              </>
            ) : null}

            {isAdmin ? (
              <>
                <SettingsActionsSection title="Actions">
                  {credential.type === 'oauth2' ? (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        onConnect(credential.id, credential.service)
                      }
                      disabled={isSaving}
                    >
                      Connect
                    </Button>
                  ) : null}
                  <Button
                    variant="secondary"
                    onClick={() => onEdit(credential)}
                  >
                    Edit
                  </Button>
                  {canTransfer ? (
                    <Button
                      variant="secondary"
                      data-testid={`credential-transfer-${credential.id}`}
                      onClick={() => onTransfer(credential)}
                      disabled={isSaving}
                    >
                      Transfer
                    </Button>
                  ) : null}
                  <SettingsConfirmButton
                    label="Delete"
                    confirmLabel="Confirm Delete"
                    cancelLabel="Cancel"
                    disabled={isSaving}
                    onConfirm={() => {
                      void onDelete(credential.id, credential.name).then(
                        result => {
                          if (!result.deleted && result.usages?.length) {
                            setUsageConflict(result.usages);
                          }
                        }
                      );
                    }}
                  />
                </SettingsActionsSection>
              </>
            ) : null}
          </SettingsContentSurface>
        )}

        {canManageAccess &&
          activeTab === 'access' &&
          (isAdmin ? (
            <CredentialPermissionsEditor credentialId={credential.id} />
          ) : (
            <SettingsEntityMessage>
              {t[
                'com.streamlain.settings.workspace.credentials.permissions.admin-only'
              ]()}
            </SettingsEntityMessage>
          ))}
      </SettingsTabbedContent>
    </SettingsExpandableItem>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function WorkspaceSettingCredentials() {
  const t = useI18n();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceService = useService(WorkspaceService);
  const information = useWorkspaceInfo(workspaceService.workspace);
  const isAdmin = !!(information?.isOwner || information?.isAdmin);
  const isEditor = information?.role === Permission.Editor;
  const { isFeatureAllowed } = useFeatureGate();
  const hasCredentialSharing = isFeatureAllowed('credential-sharing');

  const {
    credentials: allCredentials,
    isBackendAvailable,
    isLoading,
    isSaving,
    createCredential,
    updateCredential,
    deleteCredential,
    getCredentialUsage,
    revealCredential,
    startOAuthConnect,
    transferOwnership,
    listAdminOverview,
  } = useWorkspaceCredentials();

  // sl-bj0x.7: Filter out webhook credentials — shown under Automations tab instead
  const credentials = allCredentials.filter(c => c.service !== 'webhook');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCredential, setEditingCredential] =
    useState<WorkspaceCredentialInfo | null>(null);
  const [transferringCredential, setTransferringCredential] =
    useState<WorkspaceCredentialInfo | null>(null);
  const [adminOverviewRefreshKey, setAdminOverviewRefreshKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdminOverview, setShowAdminOverview] = useState(false);

  // sl-kqzl.3.3: CREDENTIAL_TRANSFER is available on all plans. The gate
  // check keeps the UI consistent with backend @RequireFeature and future-
  // proofs against plan-matrix changes.
  const hasCredentialTransfer = isFeatureAllowed('credential-transfer');
  const canShowTransferUi = isAdmin && hasCredentialTransfer;

  const handleCreate = useCallback(
    async (
      name: string,
      type: CredentialType,
      service: string,
      data: CredentialData
    ) => {
      await createCredential(name, type, service, data);
    },
    [createCredential]
  );

  const handleUpdate = useCallback(
    async (credentialId: string, data: CredentialData) => {
      await updateCredential(credentialId, data);
    },
    [updateCredential]
  );

  const handleDelete = useCallback(
    async (id: string, name: string, force = false) => {
      return deleteCredential(id, name, force);
    },
    [deleteCredential]
  );

  const handleTransfer = useCallback(
    async (credentialId: string, newOwnerId: string) => {
      await transferOwnership(credentialId, newOwnerId);
      setAdminOverviewRefreshKey(k => k + 1);
    },
    [transferOwnership]
  );

  const filteredCredentials = credentials.filter(credential => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      credential.name.toLowerCase().includes(query) ||
      credential.service.toLowerCase().includes(query) ||
      credential.type.toLowerCase().includes(query) ||
      credential.status.toLowerCase().includes(query)
    );
  });

  return (
    <SettingsSearchableEntityPage
      title={
        t['com.streamlain.settings.workspace.credentials.title']?.() ??
        'Credentials'
      }
      subtitle={
        t['com.streamlain.settings.workspace.credentials.subtitle']?.() ??
        'Manage API keys and authentication credentials for external services. Used by automation workflows to authenticate with third-party APIs.'
      }
      headerAction={<SettingsOfflineHeaderAction show={!isBackendAvailable} />}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder={
        t[
          'com.streamlain.settings.workspace.credentials.search-placeholder'
        ]?.() ?? 'Credentials suchen...'
      }
      action={
        <SettingsActionRow>
          {canShowTransferUi ? (
            <Button
              variant="secondary"
              onClick={() => setShowAdminOverview(v => !v)}
            >
              {showAdminOverview
                ? (t[
                    'com.streamlain.settings.workspace.credentials.back-to-list'
                  ]?.() ?? 'Zur Liste')
                : (t[
                    'com.streamlain.settings.workspace.credentials.admin-overview'
                  ]?.() ?? 'Admin-Übersicht')}
            </Button>
          ) : null}
          {isAdmin ? (
            <Button
              variant="primary"
              onClick={() => setShowAddModal(true)}
              disabled={isSaving || !isBackendAvailable}
            >
              {t[
                'com.streamlain.settings.workspace.credentials.add-button'
              ]?.() ?? '+ Credential hinzufügen'}
            </Button>
          ) : null}
        </SettingsActionRow>
      }
      loading={isLoading && isBackendAvailable}
      hasItems={showAdminOverview || filteredCredentials.length > 0}
      loadingContent={
        <SettingsEntityMessage>
          {t['com.streamlain.settings.workspace.credentials.loading']?.() ??
            'Credentials werden geladen...'}
        </SettingsEntityMessage>
      }
      emptyContent={
        <SettingsEntityMessage>
          {searchQuery.trim()
            ? (t[
                'com.streamlain.settings.workspace.credentials.empty-search'
              ]?.() ?? 'Keine Credentials für diese Suche gefunden.')
            : !isBackendAvailable
              ? offlineLabel
              : (t['com.streamlain.settings.workspace.credentials.empty']?.() ??
                'Noch keine Credentials konfiguriert.')}
        </SettingsEntityMessage>
      }
      footer={isEditor ? <SettingsEditorScopeHint /> : null}
    >
      <ConnectionSettingsSection
        key={workspaceService.workspace.id}
        workspaceId={workspaceService.workspace.id}
        readOnly={!isAdmin}
        canUse={isAdmin || isEditor}
      />
      {showAdminOverview && canShowTransferUi ? (
        <SettingsTabbedContent>
          <AdminOverviewTab
            listAdminOverview={listAdminOverview}
            getCredentialUsage={getCredentialUsage}
            onTransferRequest={setTransferringCredential}
            refreshKey={adminOverviewRefreshKey}
          />
        </SettingsTabbedContent>
      ) : (
        <SettingsEntityList>
          {filteredCredentials.map(cred => (
            <CredentialCard
              key={cred.id}
              credential={cred}
              onEdit={setEditingCredential}
              onDelete={handleDelete}
              onConnect={(credId, svc) => void startOAuthConnect(credId, svc)}
              onGetUsage={getCredentialUsage}
              onReveal={revealCredential}
              onTransfer={setTransferringCredential}
              canTransfer={canShowTransferUi}
              isAdmin={isAdmin}
              isSaving={isSaving}
              canManageAccess={hasCredentialSharing}
            />
          ))}
        </SettingsEntityList>
      )}

      {showAddModal && (
        <AddCredentialModal
          onClose={() => setShowAddModal(false)}
          onSave={handleCreate}
          isSaving={isSaving}
        />
      )}

      {editingCredential && (
        <EditCredentialModal
          credential={editingCredential}
          onClose={() => setEditingCredential(null)}
          onSave={handleUpdate}
          isSaving={isSaving}
        />
      )}

      {transferringCredential && (
        <TransferOwnershipModal
          credential={transferringCredential}
          onClose={() => setTransferringCredential(null)}
          onTransfer={handleTransfer}
          isSaving={isSaving}
        />
      )}
    </SettingsSearchableEntityPage>
  );
}
